<template>
  <v-container class="bug-panel mt-4 px-5" fluid>
    <v-row>
      <v-col cols="12">
        <div class="panel-hero">
          <div class="panel-hero-icon">
            <v-icon color="primary">mdi-bug-outline</v-icon>
          </div>
          <div>
            <div class="panel-title">Panel de fallos</div>
            <div class="panel-subtitle">Reportes automáticos y manuales del sistema RH.</div>
          </div>
          <v-spacer></v-spacer>
          <v-btn color="primary" rounded depressed :loading="loading" @click="initialize">
            <v-icon left small>mdi-refresh</v-icon>
            Actualizar
          </v-btn>
        </div>

        <v-card class="panel-card">
          <v-card-text>
            <div class="filters-header">
              <div>
                <div class="section-title">
                  <v-icon small color="primary" class="mr-1">mdi-filter-variant</v-icon>
                  Filtros de búsqueda
                </div>
                <div class="section-subtitle">Refina los problemas agrupados por estatus, severidad, módulo o usuario.</div>
              </div>
            </div>

            <v-row class="filters-card mt-3" align="center">
              <v-col cols="12" md="2">
                <v-select
                  v-model="filters.estatus"
                  :items="estatusOptions"
                  label="Estatus"
                  clearable
                  dense
                  outlined
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-select
                  v-model="filters.severidad"
                  :items="severidadOptions"
                  label="Severidad"
                  clearable
                  dense
                  outlined
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filters.modulo"
                  label="Módulo"
                  dense
                  outlined
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-view-grid-outline"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filters.usuario"
                  label="Usuario"
                  dense
                  outlined
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-account-outline"
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-btn color="primary" depressed block :loading="loading" @click="buscar">
                  <v-icon left small>mdi-magnify</v-icon>
                  Buscar
                </v-btn>
              </v-col>
            </v-row>

            <div class="table-section">
              <div class="list-toolbar">
                <div>
                  <div class="table-title">Problemas agrupados</div>
                  <div class="table-subtitle">{{ pagination.total }} problemas encontrados</div>
                </div>
              </div>

              <v-data-table
                :headers="headers"
                :items="reportes"
                :loading="loading"
                :server-items-length="pagination.total"
                :options.sync="tableOptions"
                class="bug-table elevation-0 mt-3"
                dense
                item-key="id"
                @update:options="onOptionsChange"
              >
                <template v-slot:item.id="{ item }">
                  <span class="report-id">#{{ item.id }}</span>
                </template>

                <template v-slot:item.total_reportes="{ item }">
                  <v-chip small label color="indigo" dark>
                    {{ item.total_reportes || 1 }} veces
                  </v-chip>
                </template>

                <template v-slot:item.usuarios_afectados="{ item }">
                  <v-chip small label color="cyan darken-2" dark>
                    {{ item.usuarios_afectados || 0 }} personas
                  </v-chip>
                </template>

                <template v-slot:item.modulo="{ item }">
                  <div class="font-weight-bold">{{ item.modulo || 'Sin módulo' }}</div>
                  <div class="text-caption grey--text">{{ item.endpoint || '' }}</div>
                </template>

                <template v-slot:item.ruta="{ item }">
                  <span class="route-pill">{{ item.ruta || '-' }}</span>
                </template>

                <template v-slot:item.severidad="{ item }">
                  <v-select
                    :value="item.severidad"
                    :items="severidadOptions"
                    dense
                    hide-details
                    solo
                    flat
                    class="inline-select"
                    :disabled="guardandoFila === item.id"
                    @change="actualizarCampo(item, 'severidad', $event)"
                  >
                    <template v-slot:selection="{ item: opt }">
                      <v-chip small label :color="colorSeveridad(opt.value)" dark>
                        {{ opt.text }}
                      </v-chip>
                    </template>
                  </v-select>
                </template>

                <template v-slot:item.estatus="{ item }">
                  <v-select
                    :value="mapEstatus(item.estatus)"
                    :items="estatusOptions"
                    dense
                    hide-details
                    solo
                    flat
                    class="inline-select"
                    :disabled="guardandoFila === item.id"
                    @change="actualizarCampo(item, 'estatus', $event)"
                  >
                    <template v-slot:selection="{ item: opt }">
                      <v-chip small label :color="colorEstatus(opt.value)" dark>
                        {{ opt.text }}
                      </v-chip>
                    </template>
                  </v-select>
                </template>

                <template v-slot:item.usuario_nombre="{ item }">
                  <div class="user-cell">
                    <v-avatar size="30" color="blue lighten-5" class="mr-2">
                      <v-icon small color="primary">mdi-account</v-icon>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium">{{ item.usuario_nombre || 'Sin usuario' }}</div>
                      <div class="text-caption grey--text">ERP: {{ item.id_usuario_erp || '-' }}</div>
                    </div>
                  </div>
                </template>

                <template v-slot:item.fecha_reporte="{ item }">
                  {{ formatDate(item.fecha_reporte) }}
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-btn rounded small color="primary" outlined class="mr-1" @click="abrirDetalle(item)">
                    <v-icon left small>mdi-open-in-new</v-icon>
                    Ver
                  </v-btn>
                  <v-btn
                    icon
                    small
                    color="error"
                    :loading="eliminandoId === item.id"
                    title="Eliminar reporte"
                    @click="confirmarEliminar(item)"
                  >
                    <v-icon small>mdi-delete-outline</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="detalleDialog" max-width="980" scrollable>
      <v-card v-if="detalle">
        <v-card-title>
          Problema #{{ detalle.id }}
          <v-spacer></v-spacer>
          <v-btn icon @click="detalleDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12" md="8">
              <v-alert text :color="colorSeveridad(form.severidad)">
                <div class="font-weight-bold">{{ detalle.mensaje_error || 'Reporte manual' }}</div>
                <div>{{ detalle.descripcion_usuario || 'Sin descripción del usuario' }}</div>
              </v-alert>

              <div v-if="detalle.group" class="group-summary">
                <div class="group-stat">
                  <div class="group-stat-value">{{ detalle.group.total_reportes || 1 }}</div>
                  <div class="group-stat-label">veces reportado</div>
                </div>
                <div class="group-stat">
                  <div class="group-stat-value">{{ detalle.group.usuarios_afectados || 0 }}</div>
                  <div class="group-stat-label">personas afectadas</div>
                </div>
                <div class="group-stat">
                  <div class="group-stat-value">{{ formatDate(detalle.group.ultima_fecha) }}</div>
                  <div class="group-stat-label">último reporte</div>
                </div>
              </div>

              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td class="font-weight-bold">Ruta</td>
                    <td>{{ detalle.ruta || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Endpoint</td>
                    <td>{{ detalle.metodo_http || '' }} {{ detalle.endpoint || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Usuario</td>
                    <td>{{ detalle.usuario_nombre || '-' }} / {{ detalle.puesto || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Sentry</td>
                    <td>
                      <a v-if="detalle.id_sentry_issue" :href="detalle.id_sentry_issue" target="_blank" rel="noopener">
                        {{ detalle.id_sentry_issue }}
                      </a>
                      <span v-else>-</span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <v-expansion-panels flat class="mt-4">
                <v-expansion-panel>
                  <v-expansion-panel-header>Stack / metadata</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <pre class="bug-report-pre">{{ detalle.stack_resumido || 'Sin stack' }}</pre>
                    <pre class="bug-report-pre mt-2">{{ prettyJson(detalle.metadata) }}</pre>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>

            <v-col cols="12" md="4">
              <v-select
                v-model="form.estatus"
                :items="estatusOptions"
                label="Estatus"
                filled
                dense
              >
                <template v-slot:selection="{ item }">
                  <v-chip small label :color="colorEstatus(item.value)" dark class="mr-1">
                    {{ item.text }}
                  </v-chip>
                </template>
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-chip small label :color="colorEstatus(item.value)" dark>
                      {{ item.text }}
                    </v-chip>
                  </v-list-item-content>
                </template>
              </v-select>
              <v-select
                v-model="form.severidad"
                :items="severidadOptions"
                label="Severidad"
                filled
                dense
              >
                <template v-slot:selection="{ item }">
                  <v-chip small label :color="colorSeveridad(item.value)" dark class="mr-1">
                    {{ item.text }}
                  </v-chip>
                </template>
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-chip small label :color="colorSeveridad(item.value)" dark>
                      {{ item.text }}
                    </v-chip>
                  </v-list-item-content>
                </template>
              </v-select>
              <v-autocomplete
                v-model="form.asignado_a"
                :items="usuariosAsignables"
                :loading="loadingUsuarios"
                label="Asignar a (puesto 11 o 12)"
                item-text="nombre_completo"
                item-value="id_usuario"
                filled
                dense
                clearable
                no-data-text="No hay usuarios con puesto 11 o 12"
                @change="onAsignadoChange"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.nombre_completo }}</v-list-item-title>
                    <v-list-item-subtitle>
                      ERP {{ item.id_usuario }} · Puesto {{ item.idpuesto }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
                <template v-slot:selection="{ item }">
                  {{ item.nombre_completo }} (ERP {{ item.id_usuario }})
                </template>
              </v-autocomplete>
              <v-text-field
                v-model="form.fecha_correccion"
                label="Fecha corrección"
                type="date"
                filled
                dense
              />
              <v-textarea
                v-model="form.notas_dev"
                label="Notas dev"
                filled
                dense
                rows="3"
                auto-grow
              />
              <v-btn color="primary" block :loading="saving" class="mb-2" @click="guardarCambios">
                Guardar cambios
              </v-btn>
              <v-btn
                color="error"
                outlined
                block
                :loading="eliminandoId === detalle.id"
                @click="confirmarEliminar(detalle)"
              >
                <v-icon left small>mdi-delete-outline</v-icon>
                Eliminar reporte
              </v-btn>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <div
            v-if="detalle.group && detalle.group.reportes && detalle.group.reportes.length"
            class="related-section"
          >
            <div class="table-title">Reportes relacionados</div>
            <div class="table-subtitle mb-2">
              Últimos {{ detalle.group.reportes.length }} reportes del mismo problema
            </div>
            <v-simple-table dense class="related-table">
              <thead>
                <tr>
                  <th>#ID</th>
                  <th>Usuario</th>
                  <th>Ruta</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="reporte in detalle.group.reportes" :key="reporte.id">
                  <td class="report-id">#{{ reporte.id }}</td>
                  <td>{{ reporte.usuario_nombre || 'Sin usuario' }}</td>
                  <td>{{ reporte.ruta || '-' }}</td>
                  <td>{{ formatDate(reporte.fecha_reporte) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <v-divider class="my-4"></v-divider>

          <v-row>
            <v-col cols="12" md="8">
              <v-textarea
                v-model="nota"
                label="Agregar nota al historial"
                filled
                dense
                rows="2"
                auto-grow
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-btn color="success" block :loading="savingNote" :disabled="!nota" @click="agregarNota">
                Agregar nota
              </v-btn>
            </v-col>
          </v-row>

          <v-timeline dense clipped>
            <v-timeline-item
              v-for="evento in detalle.events || []"
              :key="evento.id"
              small
              color="primary"
            >
              <div class="font-weight-bold">{{ labelEvento(evento) }}</div>
              <div class="text-caption grey--text">
                {{ formatDate(evento.created_at) }} - {{ evento.usuario_nombre || evento.usuario_erp || 'Sistema' }}
              </div>
              <div v-if="evento.nota">{{ evento.nota }}</div>
            </v-timeline-item>
          </v-timeline>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="440" persistent>
      <v-card>
        <v-card-title class="text-h6">
          Eliminar reporte
        </v-card-title>
        <v-card-text>
          ¿Eliminar el reporte
          <strong v-if="reporteEliminar">#{{ reporteEliminar.id }}</strong>?
          Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="!!eliminandoId" @click="cancelarEliminar">Cancelar</v-btn>
          <v-btn color="error" depressed :loading="!!eliminandoId" @click="eliminarReporte">
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  agregarNotaBugReport,
  actualizarBugReport,
  eliminarBugReport,
  listarBugReports,
  obtenerBugReport,
} from '@/api/bugReports'

export default {
  name: 'BugReports',

  data: () => ({
    loading: false,
    saving: false,
    savingNote: false,
    guardandoFila: null,
    eliminandoId: null,
    deleteDialog: false,
    reporteEliminar: null,
    detalleDialog: false,
    reportes: [],
    detalle: null,
    nota: '',
    usuariosAsignables: [],
    loadingUsuarios: false,
    tableOptions: {
      page: 1,
      itemsPerPage: 25,
    },
    pagination: {
      total: 0,
      page: 1,
      limit: 25,
      totalPages: 0,
    },
    filters: {
      estatus: null,
      severidad: null,
      modulo: '',
      usuario: '',
    },
    form: {
      estatus: 'nuevo',
      severidad: 'media',
      asignado_a: null,
      asignado_nombre: '',
      fecha_correccion: '',
      notas_dev: '',
    },
    headers: [
      { text: '#ID', value: 'id', sortable: false },
      { text: 'Módulo', value: 'modulo' },
      { text: 'Ruta', value: 'ruta' },
      { text: 'Usuario', value: 'usuario_nombre' },
      { text: 'Reportes', value: 'total_reportes', sortable: false },
      { text: 'Personas', value: 'usuarios_afectados', sortable: false },
      { text: 'Severidad', value: 'severidad' },
      { text: 'Estatus', value: 'estatus' },
      { text: 'Fecha reporte', value: 'fecha_reporte' },
      { text: 'Asignado', value: 'asignado_nombre' },
      { text: '', value: 'actions', sortable: false },
    ],
    estatusOptions: [
      { text: 'Nuevo', value: 'nuevo' },
      { text: 'Pendiente', value: 'pendiente' },
      { text: 'Asignado', value: 'asignado' },
      { text: 'En progreso', value: 'en_progreso' },
      { text: 'Terminado', value: 'terminado' },
    ],
    severidadOptions: [
      { text: 'Baja', value: 'baja' },
      { text: 'Media', value: 'media' },
      { text: 'Alta', value: 'alta' },
      { text: 'Crítica', value: 'critica' },
    ],
  }),

  created() {
    this.initialize()
    this.cargarUsuariosAsignables()
  },

  methods: {
    async cargarUsuariosAsignables() {
      if (this.usuariosAsignables.length) return
      this.loadingUsuarios = true
      try {
        const response = await this.$http.get('usuarios.all.erp')
        const data = Array.isArray(response.data) ? response.data : []
        this.usuariosAsignables = data
          .filter((u) => [11, 12].includes(Number(u.idpuesto)))
          .map((u) => ({
            ...u,
            nombre_completo: String(u.nombre_completo || '').toUpperCase(),
          }))
          .sort((a, b) => a.nombre_completo.localeCompare(b.nombre_completo, 'es'))
      } catch (error) {
        this.notifyError(error, 'No fue posible cargar usuarios asignables.')
      } finally {
        this.loadingUsuarios = false
      }
    },

    onAsignadoChange(idUsuario) {
      if (!idUsuario) {
        this.form.asignado_nombre = ''
        return
      }
      const usuario = this.usuariosAsignables.find(
        (u) => Number(u.id_usuario) === Number(idUsuario)
      )
      this.form.asignado_nombre = usuario ? usuario.nombre_completo : ''
      if (usuario && ['nuevo', 'pendiente'].includes(this.form.estatus)) {
        this.form.estatus = 'asignado'
      }
    },

    mapEstatus(value) {
      const legacy = {
        revision: 'en_progreso',
        correccion: 'en_progreso',
        resuelto: 'terminado',
        cerrado: 'terminado',
      }
      const raw = String(value || 'nuevo').toLowerCase()
      return legacy[raw] || raw
    },

    async initialize() {
      await this.cargarReportes()
    },

    async cargarReportes() {
      this.loading = true
      try {
        const params = {
          ...this.filters,
          page: this.tableOptions.page,
          limit: this.tableOptions.itemsPerPage,
        }
        const response = await listarBugReports(params)
        this.reportes = response.data.data || []
        this.pagination = response.data.pagination || this.pagination
      } catch (error) {
        this.notifyError(error, 'No fue posible consultar los reportes.')
      } finally {
        this.loading = false
      }
    },

    buscar() {
      this.tableOptions.page = 1
      this.cargarReportes()
    },

    onOptionsChange() {
      this.cargarReportes()
    },

    async abrirDetalle(item) {
      try {
        await this.cargarUsuariosAsignables()
        const response = await obtenerBugReport(item.id)
        this.detalle = response.data.data
        this.form = {
          estatus: this.mapEstatus(this.detalle.estatus) || 'nuevo',
          severidad: this.detalle.severidad || 'media',
          asignado_a: this.detalle.asignado_a ? Number(this.detalle.asignado_a) : null,
          asignado_nombre: this.detalle.asignado_nombre || '',
          fecha_correccion: this.detalle.fecha_correccion ? String(this.detalle.fecha_correccion).slice(0, 10) : '',
          notas_dev: this.detalle.notas_dev || '',
        }
        this.nota = ''
        this.detalleDialog = true
      } catch (error) {
        this.notifyError(error, 'No fue posible abrir el reporte.')
      }
    },

    async guardarCambios() {
      if (!this.detalle) return
      this.saving = true
      try {
        const response = await actualizarBugReport(this.detalle.id, this.form)
        this.detalle = response.data.data
        this.form.severidad = this.detalle.severidad || this.form.severidad
        this.form.estatus = this.mapEstatus(this.detalle.estatus) || this.form.estatus
        await this.initialize()
        this.$notify({ group: 'foo', title: 'Panel de fallos', text: 'Reporte actualizado.', type: 'success' })
      } catch (error) {
        this.notifyError(error, 'No fue posible actualizar el reporte.')
      } finally {
        this.saving = false
      }
    },

    async actualizarCampo(item, campo, valor) {
      if (!item || !item.id) return
      const previo = item[campo]
      item[campo] = valor
      this.guardandoFila = item.id
      try {
        const payload = {
          estatus: this.mapEstatus(item.estatus),
          severidad: item.severidad,
          asignado_a: item.asignado_a || null,
          asignado_nombre: item.asignado_nombre || '',
          fecha_correccion: item.fecha_correccion
            ? String(item.fecha_correccion).slice(0, 10)
            : '',
          notas_dev: item.notas_dev || '',
        }
        payload[campo] = campo === 'estatus' ? this.mapEstatus(valor) : valor
        const response = await actualizarBugReport(item.id, payload)
        const actualizado = response.data.data || {}
        item.estatus = actualizado.estatus || payload.estatus
        item.severidad = actualizado.severidad || payload.severidad
        if (this.detalle && this.detalle.id === item.id) {
          this.detalle = actualizado
          this.form.estatus = this.mapEstatus(actualizado.estatus)
          this.form.severidad = actualizado.severidad || this.form.severidad
        }
      } catch (error) {
        item[campo] = previo
        this.notifyError(error, 'No fue posible actualizar el reporte.')
      } finally {
        this.guardandoFila = null
      }
    },

    async agregarNota() {
      if (!this.detalle || !this.nota) return
      this.savingNote = true
      try {
        const response = await agregarNotaBugReport(this.detalle.id, this.nota)
        this.detalle = response.data.data
        this.nota = ''
        this.$notify({ group: 'foo', title: 'Panel de fallos', text: 'Nota agregada.', type: 'success' })
      } catch (error) {
        this.notifyError(error, 'No fue posible agregar la nota.')
      } finally {
        this.savingNote = false
      }
    },

    confirmarEliminar(item) {
      if (!item || !item.id) return
      this.reporteEliminar = item
      this.deleteDialog = true
    },

    cancelarEliminar() {
      this.deleteDialog = false
      this.reporteEliminar = null
    },

    async eliminarReporte() {
      if (!this.reporteEliminar || !this.reporteEliminar.id) return
      const id = this.reporteEliminar.id
      this.eliminandoId = id
      try {
        await eliminarBugReport(id)
        if (this.detalle && this.detalle.id === id) {
          this.detalleDialog = false
          this.detalle = null
        }
        this.cancelarEliminar()
        await this.initialize()
        this.$notify({
          group: 'foo',
          title: 'Panel de fallos',
          text: `Reporte #${id} eliminado.`,
          type: 'success',
        })
      } catch (error) {
        this.notifyError(error, 'No fue posible eliminar el reporte.')
      } finally {
        this.eliminandoId = null
      }
    },

    notifyError(error, fallback) {
      const message = error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : fallback
      this.$notify({ group: 'foo', title: 'Panel de fallos', text: message, type: 'error' })
    },

    labelEstatus(value) {
      const mapped = this.mapEstatus(value)
      const found = this.estatusOptions.find((item) => item.value === mapped)
      return found ? found.text : mapped || '-'
    },

    labelSeveridad(value) {
      const found = this.severidadOptions.find((item) => item.value === value)
      return found ? found.text : value || '-'
    },

    colorEstatus(value) {
      const mapped = this.mapEstatus(value)
      return {
        nuevo: 'blue',
        pendiente: 'amber darken-2',
        asignado: 'indigo',
        en_progreso: 'orange',
        terminado: 'green',
      }[mapped] || 'grey'
    },

    colorSeveridad(value) {
      return {
        baja: 'green',
        media: 'blue',
        alta: 'orange',
        critica: 'red',
      }[value] || 'grey'
    },

    formatDate(value) {
      if (!value) return '-'
      return String(value).replace('T', ' ').slice(0, 19)
    },

    prettyJson(value) {
      if (!value) return 'Sin metadata'
      try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value
        return JSON.stringify(parsed, null, 2)
      } catch (error) {
        return String(value)
      }
    },

    labelEvento(evento) {
      if (evento.tipo === 'nota') return 'Nota'
      if (evento.tipo === 'creacion') return 'Creación'
      if (evento.estatus_anterior !== evento.estatus_nuevo) {
        return `${this.labelEstatus(evento.estatus_anterior)} → ${this.labelEstatus(evento.estatus_nuevo)}`
      }
      return 'Actualización'
    },
  },
}
</script>

<style scoped>
.bug-panel {
  background:
    radial-gradient(circle at 12% 0%, rgba(37, 99, 235, 0.12), transparent 30%),
    radial-gradient(circle at 88% 0%, rgba(14, 165, 233, 0.10), transparent 28%),
    linear-gradient(180deg, #eef5ff 0%, #f7faff 48%, #f9fbff 100%);
  min-height: calc(100vh - 72px);
}

.panel-hero {
  align-items: center;
  background:
    linear-gradient(135deg, rgba(37, 99, 235, 0.16) 0%, rgba(14, 165, 233, 0.12) 48%, rgba(168, 85, 247, 0.10) 100%),
    linear-gradient(180deg, #ffffff 0%, #f7fbff 100%);
  border: 1px solid #d6e6ff;
  border-radius: 22px;
  box-shadow: 0 18px 44px rgba(37, 99, 235, 0.11);
  display: flex;
  gap: 14px;
  margin-bottom: 18px;
  padding: 22px 24px;
}

.panel-hero-icon {
  align-items: center;
  background: #ffffff;
  border: 1px solid #bfdbfe;
  border-radius: 16px;
  box-shadow: 0 10px 24px rgba(37, 99, 235, 0.15);
  display: flex;
  height: 48px;
  justify-content: center;
  width: 48px;
}

.panel-title {
  color: #172033;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.panel-subtitle {
  color: #64748b;
  font-size: 14px;
  margin-top: 2px;
}

.panel-card {
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #dbe7f6;
  border-radius: 22px;
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.08);
  overflow: hidden;
}

.filters-header {
  align-items: center;
  background: #ffffff;
  border: 1px solid #e5edf8;
  border-radius: 16px;
  display: flex;
  justify-content: space-between;
  padding: 14px 16px;
}

.filters-card {
  background: #f7fbff;
  border: 1px solid #d6e6ff;
  border-radius: 18px;
  margin-left: 0;
  margin-right: 0;
  padding: 18px 12px 12px;
}

.list-toolbar {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.section-title {
  align-items: center;
  color: #172033;
  display: flex;
  font-size: 15px;
  font-weight: 800;
}

.section-subtitle {
  color: #64748b;
  font-size: 12px;
  margin-top: 2px;
}

.table-section {
  border-top: 1px solid #e5edf8;
  margin-top: 24px;
  padding-top: 18px;
}

.table-title {
  color: #172033;
  font-size: 18px;
  font-weight: 800;
}

.table-subtitle {
  color: #64748b;
  font-size: 13px;
}

.bug-table {
  background: #ffffff;
  border: 1px solid #dbe7f6;
  border-radius: 18px;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.05);
  overflow: hidden;
}

.bug-table::v-deep thead tr {
  background: #eef6ff;
}

.bug-table::v-deep tbody tr:nth-child(even) {
  background: #f8fbff;
}

.bug-table::v-deep tbody tr:hover {
  background: #edf7ff !important;
}

.report-id {
  color: #2563eb;
  font-weight: 800;
}

.route-pill {
  background: #e0f2fe;
  border: 1px solid #bae6fd;
  border-radius: 999px;
  color: #0369a1;
  display: inline-block;
  max-width: 260px;
  overflow: hidden;
  padding: 4px 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-cell {
  align-items: center;
  display: flex;
}

.group-summary {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 16px;
}

.group-stat {
  background: #f7fbff;
  border: 1px solid #dbeafe;
  border-radius: 14px;
  padding: 12px 14px;
}

.group-stat-value {
  color: #0f172a;
  font-size: 18px;
  font-weight: 800;
}

.group-stat-label {
  color: #64748b;
  font-size: 12px;
  margin-top: 2px;
}

.related-section {
  background: #fbfdff;
  border: 1px solid #e5edf8;
  border-radius: 16px;
  padding: 14px;
}

.related-table {
  border: 1px solid #edf2f7;
  border-radius: 12px;
  overflow: hidden;
}

.bug-report-pre {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 12px;
  max-height: 240px;
  overflow: auto;
  padding: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}

.inline-select {
  max-width: 170px;
}

.inline-select::v-deep .v-input__slot {
  min-height: 32px !important;
  padding: 0 4px !important;
}

.inline-select::v-deep .v-select__selections input {
  display: none;
}
</style>
